import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Image, FileText, Video, Upload, CheckCircle, Clock, AlertTriangle,
  BarChart3, Bell, Plus, Eye, ChevronLeft, ChevronRight, CalendarDays, RefreshCw
} from 'lucide-react';

const API = '/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600', icon: FileText },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700', icon: Clock },
  pending_admin_review: { label: 'Pending Admin Review', color: 'bg-purple-100 text-purple-700', icon: Clock },
  changes_requested: { label: 'Changes Needed', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600', icon: AlertTriangle },
};

const TYPE_ICON = { image: Image, video: Video, blog: FileText };

const PLATFORM_COLORS = {
  instagram: 'bg-pink-100 text-pink-700',
  facebook: 'bg-blue-100 text-blue-700',
  linkedin: 'bg-sky-100 text-sky-700',
  twitter: 'bg-slate-100 text-slate-700',
  website: 'bg-violet-100 text-violet-700',
};

const STATUS_DOT = {
  under_review: 'bg-blue-500',
  pending_admin_review: 'bg-purple-500',
  changes_requested: 'bg-amber-500',
  approved: 'bg-emerald-500',
  rejected: 'bg-red-500',
  draft: 'bg-slate-400',
};

function KpiCard({ title, value, icon: Icon, color, loading }) {
  if (loading) return <Skeleton className="h-24 rounded-xl" />;
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>;
}

// ─── ANALYTICS DUE BANNER ─────────────────────────────────
function AnalyticsDueBanner({ dueItems, navigate }) {
  if (!dueItems || dueItems.length === 0) return null;
  const overdue = dueItems.filter(d => d.days_overdue > 0 && !d.has_analytics);
  const pending = dueItems.filter(d => !d.has_analytics);
  if (pending.length === 0) return null;

  return (
    <div className={`rounded-xl border p-4 ${overdue.length > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="analytics-due-banner">
      <div className="flex items-start gap-3">
        <BarChart3 className={`h-5 w-5 mt-0.5 shrink-0 ${overdue.length > 0 ? 'text-red-500' : 'text-amber-500'}`} />
        <div className="flex-1">
          <p className={`text-sm font-semibold ${overdue.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
            {pending.length} post{pending.length > 1 ? 's' : ''} need analytics check-in
          </p>
          <div className="mt-2 space-y-1">
            {pending.slice(0, 3).map(item => (
              <button
                key={item.content_id}
                onClick={() => navigate(`/social/content/${item.content_id}`)}
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-600 transition-colors w-full text-left"
                data-testid={`due-item-${item.content_id}`}
              >
                <span className="truncate max-w-[200px]">{item.title}</span>
                {item.days_overdue > 0 ? (
                  <Badge className="bg-red-100 text-red-700 text-[10px]">{item.days_overdue}d overdue</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">due now</Badge>
                )}
              </button>
            ))}
            {pending.length > 3 && (
              <p className="text-xs text-slate-500">+{pending.length - 3} more</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ────────────────────────────────────────
export default function SocialDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [overview, setOverview] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [dueItems, setDueItems] = useState([]);
  const limit = 15;

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (tab !== 'all') params.status = tab;
      const { data } = await axios.get(`${API}/social/content`, { params, withCredentials: true });
      setItems(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  useEffect(() => { fetchContent(); }, [fetchContent]);
  useEffect(() => { setPage(1); }, [tab]);

  useEffect(() => {
    axios.get(`${API}/social/analytics/overview`, { withCredentials: true })
      .then(r => setOverview(r.data)).catch(() => {});
    axios.get(`${API}/social/notifications`, { withCredentials: true })
      .then(r => setNotifications(r.data)).catch(() => {});
    axios.get(`${API}/social/analytics-due`, { withCredentials: true })
      .then(r => setDueItems(r.data.due_items || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="social-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Social Media Hub</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your content, approvals, and performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/social/upload')} data-testid="new-post-btn">
            <Plus className="h-4 w-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      {/* Analytics Due Banner */}
      <AnalyticsDueBanner dueItems={dueItems} navigate={navigate} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard title="Approved Posts" value={overview?.total_approved_posts || 0} icon={CheckCircle} color="bg-emerald-500" loading={!overview} />
        <KpiCard title="Total Impressions" value={(overview?.total_impressions || 0).toLocaleString()} icon={Eye} color="bg-blue-500" loading={!overview} />
        <KpiCard title="Total Engagement" value={(overview?.total_engagement || 0).toLocaleString()} icon={BarChart3} color="bg-violet-500" loading={!overview} />
        <KpiCard title="Analytics Pending" value={overview?.pending_analytics || 0} icon={Clock} color="bg-amber-500" loading={!overview} />
      </div>

      {/* View Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline" size="sm"
          onClick={() => navigate('/social/calendar')}
          data-testid="view-calendar-btn"
        >
          <CalendarDays className="h-4 w-4 mr-1" /> Open Calendar
        </Button>
      </div>

      {/* Content List View */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Your Content</CardTitle>
              <Badge variant="outline">{total} posts</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <div className="px-4 border-b border-slate-100">
                <TabsList className="bg-transparent h-10 gap-2">
                  <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                  <TabsTrigger value="under_review" className="text-xs">Under Review</TabsTrigger>
                  <TabsTrigger value="pending_admin_review" className="text-xs">Pending Admin</TabsTrigger>
                  <TabsTrigger value="changes_requested" className="text-xs">Changes Needed</TabsTrigger>
                  <TabsTrigger value="approved" className="text-xs">Approved</TabsTrigger>
                </TabsList>
              </div>

              {loading ? (
                <div className="p-6 space-y-3">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                </div>
              ) : items.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <Upload className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No content yet</p>
                  <p className="text-sm mt-1">Upload your first post to get started</p>
                  <Button className="mt-4" onClick={() => navigate('/social/upload')}>
                    <Plus className="h-4 w-4 mr-2" /> Create Post
                  </Button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead className="text-xs w-12">#</TableHead>
                          <TableHead className="text-xs w-12">Media</TableHead>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs">Caption / Title</TableHead>
                          <TableHead className="text-xs">Platforms</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">AI Score</TableHead>
                          <TableHead className="text-xs">Revision</TableHead>
                          <TableHead className="text-xs">Updated</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, idx) => {
                          const TypeIcon = TYPE_ICON[item.content_type] || FileText;
                          return (
                            <TableRow
                              key={item.content_id}
                              className="cursor-pointer hover:bg-slate-50 transition-colors"
                              data-testid={`content-row-${item.content_id}`}
                              onClick={() => navigate(`/social/content/${item.content_id}`)}
                            >
                              <TableCell className="text-xs text-slate-400">{(page - 1) * limit + idx + 1}</TableCell>
                              <TableCell>
                                {(item.file?.url || item.file?.storage_path) ? (
                                  item.content_type === 'video' ? (
                                    <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center">
                                      <TypeIcon className="h-4 w-4 text-white" />
                                    </div>
                                  ) : (
                                    <img src={item.file.url || `/api/social/media/${item.file.storage_path}`} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                                  )
                                ) : (
                                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                                    <TypeIcon className="h-4 w-4 text-slate-300" />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell><TypeIcon className="h-4 w-4 text-slate-500" /></TableCell>
                              <TableCell className="max-w-[250px] truncate text-sm font-medium text-slate-700">
                                {item.blog_title || item.caption?.slice(0, 60) || 'Untitled'}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1 flex-wrap">
                                  {(item.platforms || []).map(p => (
                                    <Badge key={p} className={`${PLATFORM_COLORS[p] || 'bg-slate-100'} text-[10px] px-1.5`}>
                                      {p}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell><StatusBadge status={item.status} /></TableCell>
                              <TableCell>
                                {item.latest_ai_review?.score != null ? (
                                  <span className={`text-sm font-bold ${item.latest_ai_review.score >= 70 ? 'text-emerald-600' : item.latest_ai_review.score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                                    {item.latest_ai_review.score}
                                  </span>
                                ) : <span className="text-xs text-slate-400">-</span>}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">v{item.revision_number || 1}</TableCell>
                              <TableCell className="text-xs text-slate-400">{item.updated_at?.split('T')[0]}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                      <p className="text-xs text-slate-400">Page {page} of {totalPages}</p>
                      <div className="flex gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Tabs>
          </CardContent>
        </Card>
    </div>
  );
}
